import { getJobsMeta } from 'src/services/jobs'
import { kebabCaseToTitleCase } from '@/utils/static-helpers/string'
import {
  castArray,
  compact,
  find,
  get,
  identity,
  isEmpty,
  isUndefined,
  join,
  kebabCase,
  map,
  pickBy,
  set,
  toLower
} from 'lodash'
import getTranslations from '@/utils/lib/get-translations'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useRecoilState, useRecoilValue } from 'recoil'
import { jobsMeta as jobsMetaAtom, user as userAtom } from 'src/atoms'
import { SeoMetaType } from 'src/types/seo'
import { useEffect, useMemo, useState } from 'react'
import { CorporateJobResponse, JobsMeta } from '@qureos/types'
import { User, UserRole } from 'src/types/shared/graphql'
import { useTranslation } from 'react-i18next'
import useJobFilters from 'src/hooks/data/useJobFilters'
import { getJobSeoMeta } from '@/utils/seo/job'
import { isLoggedIn } from '@qureos/lib'
import PublicJobs from '@/components/jobs/PublicJobs'
import clsx from 'clsx'
import { MainTemplate } from '@/components/shared/templates'
import DashboardTemplateV2 from '@/components/shared/templates/DashboardTemplateV2'
import { fetchMetaScope } from '@/utils/seo/metadata-helper'

interface JobsPageProps {
  filterOptions: JobsMeta
  seoMeta: SeoMetaType
  useNoIndexFollow?: boolean
  theme?: string[]
  title?: string
  searchSlug?: string
  location?: string
}

const JobSearchPage: NextPage<JobsPageProps> = ({
  filterOptions,
  seoMeta,
  theme,
  title,
  searchSlug,
  location,
  useNoIndexFollow = false
}) => {
  const router = useRouter()
  const user = useRecoilValue<User>(userAtom)
  const { company: companyId } = router.query
  const [companyMetaData, setCompanyMetaData] = useState<SeoMetaType>()
  const [, setJobsMeta] = useRecoilState<JobsMeta>(jobsMetaAtom)
  const { t: Labels } = useTranslation('CorporateJobs')
  const [seoCanonical, setSeoCanonical] = useState<SeoMetaType>(seoMeta)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [selectedJobId, setSelectedJobId] = useState<string>()
  const [selectedJob, setSelectedJob] = useState<CorporateJobResponse>(null)
  const [totalJobs, setTotalJobs] = useState<number>(0)
  const { themes, filters } = useJobFilters({
    themes: filterOptions.themes,
    locations: filterOptions.locations
  })

  useMemo(() => {
    const meta = {
      selectedTheme: theme,
      allThemes: themes
    }

    if (title !== 'undefined' && !isUndefined(title)) {
      set(meta, 'searchQuery', title)
    }

    if (location !== 'undefined' && !isUndefined(location)) {
      set(meta, 'selectedLocation', location)
    }

    if (selectedJobId !== 'undefined' && !isUndefined(selectedJobId)) {
      set(meta, 'jobId', selectedJobId)
    }

    if (!selectedJob?.locations?.length && !isEmpty(selectedJob?.location)) {
      set(meta, 'companyLocation', selectedJob.location)
    }

    if (selectedJob?.locations?.length === 1) {
      set(meta, 'companyLocation', selectedJob.locations?.[0]?.location)
    }

    if (
      !isEmpty(selectedJob?.company?.name) &&
      toLower(selectedJob?.company?.name) !== 'confidential'
    ) {
      set(meta, 'companyName', selectedJob?.company?.name)
    }

    if (
      !isUndefined(selectedJob?.title) &&
      toLower(selectedJob?.title) !== 'confidential'
    ) {
      set(meta, 'jobTitle', selectedJob?.title)
    }

    if (totalJobs) {
      set(meta, 'jobTotal', totalJobs)
    }

    const updatedCanonical = getJobSeoMeta(meta)
    setSeoCanonical(updatedCanonical)
  }, [location, title, theme, themes, selectedJobId, selectedJob, totalJobs])

  useEffect(() => {
    if (isLoggedIn() && user && user.role !== UserRole.Student) {
      router.push('/home')
    } else {
      setJobsMeta(filterOptions)
    }
  }, [user, filterOptions])

  useEffect(() => {
    fetchMetaScope(companyId, setCompanyMetaData)
  }, [companyId])

  const jobData = useMemo(() => {
    return (
      <div
        className={clsx('md:mx-4 mx-auto flex-1 overflow-hidden max-w-[90%]', {
          'mt-5': !isLoggedIn()
        })}
      >
        <h1 className="mb-3 text-lg font-normal">
          {searchSlug?.length ? searchSlug : 'Search'}
        </h1>
        <div
          className={clsx(
            'bg-white mt-5 mx-auto flex-1 rounded-lg shadow overflow-hidden'
          )}
        >
          <PublicJobs
            jobId={selectedJobId}
            pageNumber={Number(currentPage) || 1}
            handlePageNumberChange={setCurrentPage}
            handleJobIdChange={setSelectedJobId}
            selectedJob={selectedJob}
            setSelectedJob={setSelectedJob}
            setTotalJobs={setTotalJobs}
            filters={filters}
          />
        </div>
      </div>
    )
  }, [selectedJobId, currentPage, seoCanonical, selectedJob])

  const seoTitle =
      seoCanonical?.title || companyMetaData?.title || Labels('ALL_JOBS'),
    seoDescription = seoCanonical?.description || companyMetaData?.description
  if (!isLoggedIn()) {
    return (
      <MainTemplate
        title={seoTitle}
        meta={seoDescription}
        useNoIndexFollow={useNoIndexFollow && totalJobs > 5}
        showBreadCrumbs
        searchSlug={searchSlug}
        metaCanonical={seoCanonical?.canonical}
        seoOgMeta={seoCanonical?.ogMeta}
      >
        {jobData}
      </MainTemplate>
    )
  }

  return (
    <DashboardTemplateV2
      title={seoTitle}
      searchSlug={searchSlug}
      meta={seoDescription}
      metaCanonical={seoMeta?.canonical}
      seoOgMeta={seoMeta?.ogMeta}
      showBreadCrumbs
      useNoIndexFollow={useNoIndexFollow && totalJobs > 5}
      className="flex flex-1 bg-qureosBgGray justify-center"
    >
      {jobData}
    </DashboardTemplateV2>
  )
}

export default JobSearchPage

export async function getServerSideProps(context) {
  try {
    const [modifiedProps, filterOptions] = await Promise.all([
      getTranslations(context),
      getJobsMeta({
        bearerToken: context.req.cookies.auth_token
      })
    ])
    const { translations, query } = modifiedProps
    const { jobId, datePosted, easyApply } = query || {}
    const { params, res } = context

    const filterSlug =
      params?.slug && params?.slug !== '<no source>'
        ? (params?.slug.split('?')[0] as string)
        : ''

    /* regex might be slow for a very long url so adding a character limit */
    const MAXIMUM_SLUG_LENGTH = 100
    const redirectConfig = {
      permanent: false,
      destination: `/jobs/search/${filterSlug}`
    }

    if (filterSlug.length > MAXIMUM_SLUG_LENGTH) {
      return {
        redirect: redirectConfig
      }
    }

    const regexes = [
      /(?<theme>.*)-jobs-near-me$/,
      /^()in-(?<location>.*)/,
      /(?<theme>.*)-jobs-in-(?<location>.*)/,
      /(?<theme>.*)-in-(?<location>.*)/
    ]

    let themeLabel: string,
      themeSlug: string,
      themeId: string,
      locations: string,
      matchFound = false

    regexes.some(reg => {
      const match = reg.exec(filterSlug)
      if (match) {
        ;[, themeLabel, locations] = match.map(kebabCaseToTitleCase)
        themeSlug = match[1]
        matchFound = true
        return true
      }
      return false
    })

    if (!matchFound) {
      return {
        redirect: redirectConfig
      }
    }

    if (filterSlug?.includes('-jobs-')) {
      themeId = get(
        find(
          filterOptions.themes,
          theme =>
            theme._id === themeSlug || kebabCase(theme.label) === themeSlug
        ),
        '_id',
        undefined
      )
    }

    const searchQuery = !themeId ? themeLabel : undefined

    const seoMeta = getJobSeoMeta({
      searchQuery: searchQuery,
      selectedLocation: locations,
      selectedTheme: themeId,
      allThemes: filterOptions?.themes,
      jobId: jobId?.length ? (jobId as string) : ''
    })
    const queryParams = pickBy(
      {
        query: searchQuery,
        theme: themeId,
        location: locations
      },
      identity
    )
    const useNoIndexFollow =
      !isEmpty(searchQuery) ||
      !isEmpty(themeId) ||
      !isEmpty(locations) ||
      !isEmpty(datePosted) ||
      !isEmpty(easyApply)

    const queryString = map(
      queryParams,
      (value, key) => `${key}=${encodeURIComponent(value)}`
    ).join('&')

    const customUrl = `${redirectConfig.destination}${queryString ? '?' + queryString : ''}`

    if (customUrl !== context?.req?.url) {
      res.writeHead(302, {
        location: customUrl
      })
      res.end()
    }
    return {
      props: {
        filterOptions,
        seoMeta,
        searchSlug: join(
          compact([themeLabel?.length ? themeLabel : undefined, '', locations]),
          ' - '
        ),
        ...pickBy(
          {
            title: !themeId ? (themeLabel as string) : undefined,
            theme: themeId ? castArray(themeId) : undefined,
            location: locations ? decodeURI(locations as string) : undefined
          },
          identity
        ),
        ...translations,
        useNoIndexFollow: useNoIndexFollow
      }
    }
  } catch (error) {
    console.error('Error in getServerSideProps:', error)
    return {
      redirect: { permanent: false, destination: `/jobs` }
    }
  }
}
